import React, { useEffect, useContext, useState } from 'react';
import { connect } from 'react-redux';
import { Field, WrappedFieldProps } from 'redux-form';
import { trim, get, debounce } from 'lodash';
import styled from 'styled-components';
import Preloader from '@omilia/preloader';
import { H6 } from '@omilia/typography';
import { colors } from '@omilia/theme';
import { AlertBox } from '@omilia/alert';

import FORM_NAMES from 'constants/formNames';
import messages from 'constants/messages';
// @ts-ignore
import Toggler from 'components/Toggler';
import TextFieldReduxWrapper from 'components/TextFieldReduxWrapper';
import useToggle from 'utils/hooks/useToggle';
import { SAutoCompleteMenuWrapper } from 'components/styled';
import FormWrapper, {
  FormContext,
} from 'scenes/Projects/scenes/CreateProjectModal/components/FormWrapper';
import {
  NewProjectProps,
  FormValuesType,
  AccountTypeForm,
  TemplateTypeForm,
} from 'scenes/Projects/types';
import { PartialRootState } from 'store/services/projectTemplates/types';
// @ts-ignore
import { addProject } from 'store/services/projects/actions';
import {
  getProjectTemplatesList,
  getProjectTemplatesIsLoading,
} from 'store/services/projectTemplates/selectors';
import { fetchProjectTemplatesList } from 'store/services/projectTemplates/actions';

import { newProjectValidate } from '../utils/validate';

const SToggleWrapper = styled.div`
  align-items: center;
  display: flex;
  margin-bottom: 26px;
  margin-right: 35px;
`;

const SH6 = styled(H6)`
  color: ${colors.COLOR_GREY_800};
  margin-right: 16px;
`;

const SToggleContainer = styled.div`
  display: flex;
  flex-direction: vertical;
  flex: 1;
`;

const renderAutocomplete = (props: WrappedFieldProps) => (
  <SAutoCompleteMenuWrapper {...props} />
);

const NewProject = ({
  accounts,
  addProject,
  getAccounts,
  onCloseRequest,
  isLoadingProjectTemplates,
  isLoadingAccounts,
  projectTemplatesList,
  cancelGetAccounts,
  onSetProjectType,
  fetchProjectTemplatesList,
}: NewProjectProps) => {
  const { onChange, formValues } = useContext(FormContext);
  const [isTemplateExpanded, { toggle: toggleTemplate }] = useToggle(false);
  const [isFlowExpanded, { toggle: toggleFlow }] = useToggle(false);
  const [isTemplateInvalid, setIsTemplateInvalid] = useState(false);
  const debouncedGetAccounts = debounce(getAccounts, 400);

  useEffect(() => {
    onSetProjectType('create-project');
  }, [onSetProjectType]);

  const handleTemplateToggle = (value: boolean) => {
    if (value) {
      fetchProjectTemplatesList();
      setIsTemplateInvalid(true);
    } else {
      setIsTemplateInvalid(false);
    }
    toggleTemplate();
  };

  const handleSelectAccountItem = (item: AccountTypeForm) =>
    onChange('account', item);

  const handleSelectTemplate = (item: TemplateTypeForm) => {
    onChange('template', item);
    setIsTemplateInvalid(false);
  };

  const getAccountsCancelingPreviousRequest = (value = '') => {
    cancelGetAccounts();
    debouncedGetAccounts(value);
  };

  const handleSubmit = async ({ name, account, template }: FormValuesType) => {
    await addProject({
      name,
      account: account.slug,
      template: isTemplateExpanded && template ? template.id : undefined,
      flow2: isFlowExpanded,
    });

    onCloseRequest();
  };

  return (
    <FormWrapper
      form={FORM_NAMES.PROJECT.NEW}
      isSubmitDisabled={isTemplateInvalid}
      submitBtnName="Create"
      validate={newProjectValidate}
      onCloseRequest={onCloseRequest}
      onSubmitForm={handleSubmit}
    >
      <Field
        autoFocus
        component={TextFieldReduxWrapper}
        data-test="name"
        label="Project Name"
        name="name"
        normalize={trim}
        placeholder="Please type a name for the project"
        type="text"
      />

      <Field
        component={renderAutocomplete}
        data-test="account"
        isLoading={isLoadingAccounts}
        item={get(formValues, 'account.name', '')}
        items={accounts}
        label="Select Organization"
        name="account"
        placeholder="Select Organization"
        selectItem={handleSelectAccountItem}
        onChangeValue={getAccountsCancelingPreviousRequest}
        onOpen={getAccountsCancelingPreviousRequest}
      />

      <SToggleContainer>
        <SToggleWrapper>
          <SH6>USE TEMPLATE</SH6>
          <Toggler
            isDisabled={isFlowExpanded}
            value={isTemplateExpanded}
            onToggle={handleTemplateToggle}
          />
        </SToggleWrapper>

        <SToggleWrapper>
          <SH6>FLOW 2 BETA</SH6>
          <Toggler
            isDisabled={isTemplateExpanded}
            value={isFlowExpanded}
            onToggle={toggleFlow}
          />
        </SToggleWrapper>
      </SToggleContainer>

      {isLoadingProjectTemplates && <Preloader />}
      {isTemplateExpanded && !isLoadingProjectTemplates && (
        <Field
          component={renderAutocomplete}
          data-test="use-template"
          isLoading={isLoadingProjectTemplates}
          item={get(formValues, 'template.name', '')}
          items={projectTemplatesList}
          label="Select template"
          name="template"
          placeholder="Choose template"
          selectItem={handleSelectTemplate}
        />
      )}
      {isFlowExpanded && (
        <AlertBox
          title={messages.PROJECTS.FLOW_2.ALERT_TITLE}
          variant="warning"
        >
          {messages.PROJECTS.FLOW_2.ALERT_DESCRIPTION}
        </AlertBox>
      )}
    </FormWrapper>
  );
};

const mapStateToProps = (state: PartialRootState) => ({
  projectTemplatesList: getProjectTemplatesList(state),
  isLoadingProjectTemplates: getProjectTemplatesIsLoading(state),
});

const mapDispatchToProps = {
  fetchProjectTemplatesList,
  addProject,
};

export default connect(mapStateToProps, mapDispatchToProps)(NewProject);
